import React, { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, useSlotGroup, useGallery, useTokens } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { relativeLuminance } from '../utils/luminance';
import { CTA_SOLID, CtaStyles, ctaOverPrimary } from './_cta';

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

// ============================================================================
// Bedroom category helpers
// ============================================================================

interface BedroomCategory {
  key: string;
  label: string;
  subtitle: string;
  param: string;
  count: number;
  priceRange: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

// Bedroom labels rendered in the accent (Color 3) theme color.
const ACCENT_LABEL_KEYS = new Set(['studio', 'one', 'two']);

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

function keyToLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bedroom',
    two: '2 Bedroom',
    three: '3 Bedroom',
    four: '4 Bedroom',
    five: '5 Bedroom',
  };
  return labels[key] || `${key} Bedroom`;
}

function keyToShortLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bed',
    two: '2 Bed',
    three: '3 Bed',
    four: '4 Bed',
    five: '5 Bed',
  };
  return labels[key] || `${key} Bed`;
}

/** Word-style label shown beneath each circle, e.g. "ONE BEDROOM". */
function keyToWordLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: 'One Bedroom',
    two: 'Two Bedroom',
    three: 'Three Bedroom',
    four: 'Four Bedroom',
    five: 'Five Bedroom',
  };
  return labels[key] || `${key} Bedroom`;
}

/** Large outlined numeral overlaid on each circle ("S" for studios). */
function keyToNumber(key: string): string {
  if (key === 'studio') return 'S';
  const idx = BED_ORDER.indexOf(key);
  return idx > 0 ? String(idx) : key;
}

function formatPriceRange(plans: Floorplan[]): string {
  const prices = plans
    .flatMap((p) => [p.floorplan_price_min, p.floorplan_price_max])
    .map((v) => (v ? parseInt(String(v), 10) : NaN))
    .filter((v) => !isNaN(v) && v > 0);
  if (!prices.length) return 'Call for Pricing';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return `From $${min.toLocaleString()}`;
  return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
}

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, Floorplan[]> = {};

  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    if (!groups[key]) groups[key] = [];
    groups[key].push(plan);
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => {
    const plans = groups[key];
    const count = plans.reduce((sum, p) => {
      const units = p.floorplan_available_units ? parseInt(String(p.floorplan_available_units), 10) : 0;
      return sum + (isNaN(units) ? 0 : units);
    }, 0);
    return {
      key,
      label: keyToLabel(key),
      subtitle: keyToShortLabel(key),
      param: key,
      count,
      priceRange: formatPriceRange(plans),
    };
  });
}

// ============================================================================
// Interior photo selection from gallery
// ============================================================================

const INTERIOR_KEYWORDS = [
  'interior', 'apartment', 'unit', 'bedroom', 'living room', 'living',
  'kitchen', 'bathroom', 'bath', 'inside', 'home', 'den', 'dining',
  'closet', 'counter', 'cabinet', 'window', 'floor', 'carpet',
  'hardwood', 'granite', 'stainless', 'appliance', 'washer', 'dryer',
];

function scoreInterior(photo: PhotoGallery): number {
  const text = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!text) return 0;
  return INTERIOR_KEYWORDS.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
}

/**
 * Strips query params and trailing slashes so CDN variants of
 * the same image (e.g. ?width=400 vs ?width=800) are treated as one.
 */
function normalizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return (u.origin + u.pathname).replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.toLowerCase().split('?')[0].replace(/\/+$/, '');
  }
}

/**
 * Picks the best interior photo gallery indices for the floorplan cards.
 * Returns indices into the original gallery array so they can be used
 * as `defaultIndex` in slot group sourceBindings.
 */
function pickInteriorPhotoIndices(gallery: PhotoGallery[], count: number): number[] {
  if (!gallery.length) return Array.from({ length: count }, (_, i) => i);

  // Score every gallery item for "interior" relevance, deduplicate by URL
  const seen = new Set<string>();
  const scored: { originalIndex: number; score: number }[] = [];
  for (let i = 0; i < gallery.length; i++) {
    const photo = gallery[i];
    if (!photo.image) continue;
    const key = normalizeUrl(photo.image);
    if (seen.has(key)) continue;
    seen.add(key);
    scored.push({ originalIndex: i, score: scoreInterior(photo) });
  }

  // Sort by score descending
  scored.sort((a, b) => b.score - a.score);

  // Take the top-scored unique photos, then spread them out to avoid
  // visually similar adjacent photos (e.g. 3 kitchen shots in a row).
  const pool = scored.slice(0, Math.max(count * 2, 8));
  if (pool.length <= count) {
    const indices = pool.map((p) => p.originalIndex);
    // Pad with sequential indices if gallery is small
    while (indices.length < count) {
      indices.push(indices.length < gallery.length ? indices.length : 0);
    }
    return indices;
  }

  // Pick every Nth item to space them out visually
  const step = pool.length / count;
  const picked: number[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.min(Math.floor(i * step), pool.length - 1);
    picked.push(pool[idx].originalIndex);
  }
  return picked;
}

/**
 * Hook that selects the 5 best interior photo gallery indices for the
 * floorplan card images. These are used as smart defaults for the
 * overridable image slot groups.
 */
function useSmartInteriorIndices(): [number, number, number, number, number] {
  const gallery = useGallery();
  return useMemo(() => {
    const indices = pickInteriorPhotoIndices(gallery, 5);
    return indices as [number, number, number, number, number];
  }, [gallery]);
}

// ============================================================================
// Slot group helpers for overridable card images
// ============================================================================

/** Creates a dynamic slot group definition for a card image at a given gallery index. */
function makeCardImageSlotDef(label: string, index: number): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${index}].alt_text` },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

// Static slot group definitions for contentSchema (must use fixed default indices)
// Per-card generators: each floorplan circle should get a distinct, standout
// interior photo from the property's gallery, chosen to suit that card's floor
// plan size. Cards are ordered ascending by bedroom count (see
// groupFloorplansByBeds), so card 0 is the smallest floor plan and card 4 the
// largest — each prompt names that ordinal position and its likely bedroom
// count. The generator runs all five together and sees each field's current
// value, so it naturally spreads the selections across different rooms/shots.
// NOTE: the generator objects are inlined as literals (not built by a helper)
// so the preview system can statically extract the contentSchema.
const cardImage0Def: SlotGroupDefinition = {
  label: 'Card 1 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'Select a standout interior photo that suits the smallest floor plan (usually a studio or one-bedroom — a compact, open-plan interior).',
    context:
      'Background image for one of the circular bedroom-category cards in the Floor Plans section, ordered from fewest to most bedrooms. Each card must use a different, visually distinct photo that reflects its floor plan size.',
    sourceCollection: 'photo_gallery',
  },
};

const cardImage1Def: SlotGroupDefinition = {
  label: 'Card 2 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'Select a standout interior photo that suits the second-smallest floor plan (usually a one- or two-bedroom home).',
    context:
      'Background image for one of the circular bedroom-category cards in the Floor Plans section, ordered from fewest to most bedrooms. Each card must use a different, visually distinct photo that reflects its floor plan size.',
    sourceCollection: 'photo_gallery',
  },
};

const cardImage2Def: SlotGroupDefinition = {
  label: 'Card 3 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'Select a standout interior photo that suits the mid-size floor plan (usually a two-bedroom home).',
    context:
      'Background image for one of the circular bedroom-category cards in the Floor Plans section, ordered from fewest to most bedrooms. Each card must use a different, visually distinct photo that reflects its floor plan size.',
    sourceCollection: 'photo_gallery',
  },
};

const cardImage3Def: SlotGroupDefinition = {
  label: 'Card 4 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'Select a standout interior photo that suits the second-largest floor plan (usually a three-bedroom home with multiple rooms).',
    context:
      'Background image for one of the circular bedroom-category cards in the Floor Plans section, ordered from fewest to most bedrooms. Each card must use a different, visually distinct photo that reflects its floor plan size.',
    sourceCollection: 'photo_gallery',
  },
};

const cardImage4Def: SlotGroupDefinition = {
  label: 'Card 5 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 4, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'Select a standout interior photo that suits the largest floor plan (usually a three- or four-bedroom home — a spacious, multi-room interior).',
    context:
      'Background image for one of the circular bedroom-category cards in the Floor Plans section, ordered from fewest to most bedrooms. Each card must use a different, visually distinct photo that reflects its floor plan size.',
    sourceCollection: 'photo_gallery',
  },
};

// CTA button link — editable text + destination URL.
const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'View Floor Plans' },
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Category card sub-component (renders card image + overlay + label)
// ============================================================================

interface CategoryCardProps {
  cat: BedroomCategory;
  children: React.ReactNode; // The <EditableSlotGroup> wrapper with literal groupPath
}

function CategoryCard({ cat, children }: CategoryCardProps) {
  return (
    <a
      data-reveal
      href={`/floor-plans?bedroom=${cat.param}`}
      className="reveal-fade-up fp-card group flex flex-col items-center cursor-pointer fp-card-item"
    >
      {/* Circular image with the large outlined numeral on top.
          The glow sits behind the (overflow-clipped) circle so its
          animated shadow can extend beyond the circle's edge. */}
      <div className="fp-circle-wrap relative w-full aspect-square">
        <span className="fp-circle-glow" aria-hidden="true" />
        <div className="fp-circle relative w-full h-full rounded-full overflow-hidden">
          {children}
          <div className="fp-card-overlay-layer absolute inset-0 fp-card-overlay" />
          <div className="fp-card-number-layer absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
            <span className="fp-number font-heading">{keyToNumber(cat.key)}</span>
          </div>
        </div>
      </div>
      {/* Label beneath the circle, flanked by short rules */}
      <div className="mt-6 flex flex-col items-center">
        <span className="fp-label-rule" />
        <span
          className="fp-label-text font-body my-2.5 uppercase tracking-[0.2em] text-xs sm:text-sm font-semibold"
          style={ACCENT_LABEL_KEYS.has(cat.key) ? { color: 'var(--fp-accent-label)' } : undefined}
        >
          {keyToWordLabel(cat.key)}
        </span>
        <span className="fp-label-rule" />
      </div>
    </a>
  );
}

function CardImageContent({ img, fallbackAlt }: { img: { image?: string; alt?: string }; fallbackAlt: string }) {
  return img.image ? (
    <VibesitesImage
      url={img.image}
      alt={img.alt || fallbackAlt}
      sizes="(min-width: 1024px) 340px, (min-width: 640px) 33vw, 50vw"
      className="absolute inset-0 w-full h-full object-cover object-center"
    />
  ) : (
    <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
  );
}

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  // Smart interior photo selection — picks the best gallery indices as defaults.
  // These are used as dynamic defaultIndex values so the slot groups get
  // intelligent defaults while remaining fully overridable by the user.
  const [idx0, idx1, idx2, idx3, idx4] = useSmartInteriorIndices();

  // Runtime slot defs with smart photo indices (may differ from static schema defaults)
  const def0 = useMemo(() => makeCardImageSlotDef('Card 1 Image', idx0), [idx0]);
  const def1 = useMemo(() => makeCardImageSlotDef('Card 2 Image', idx1), [idx1]);
  const def2 = useMemo(() => makeCardImageSlotDef('Card 3 Image', idx2), [idx2]);
  const def3 = useMemo(() => makeCardImageSlotDef('Card 4 Image', idx3), [idx3]);
  const def4 = useMemo(() => makeCardImageSlotDef('Card 5 Image', idx4), [idx4]);

  // Overridable image slots — users can select/replace these via the editor
  const img0 = useSlotGroup('floorplanslist.card_image_0', def0);
  const img1 = useSlotGroup('floorplanslist.card_image_1', def1);
  const img2 = useSlotGroup('floorplanslist.card_image_2', def2);
  const img3 = useSlotGroup('floorplanslist.card_image_3', def3);
  const img4 = useSlotGroup('floorplanslist.card_image_4', def4);

  // Category cards with their image slots (one slot per circle, up to 5)
  const catSlotImages = [img0, img1, img2, img3, img4];
  const catSlotDefs = [def0, def1, def2, def3, def4];

  const ctaLink = useSlotGroup('floorplanslist.cta_link', ctaLinkDef);

  useRevealStyles();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  // When the theme's on-primary text color is dark, the property has a light
  // primary color — the branded accent label and the semi-transparent rule
  // lines read too faint against that light section background, so we swap them
  // for solid black. Otherwise keep the branded accent / on-primary treatment.
  const tokens = useTokens();
  const onPrimaryIsDark = relativeLuminance(tokens?.color?.textOnPrimary) < 0.5;
  const accentLabelColor = onPrimaryIsDark ? '#000000' : 'var(--vibe-color-accent)';
  const ruleColor = onPrimaryIsDark
    ? '#1a1a1a'
    : 'color-mix(in srgb, var(--vibe-color-text-on-primary) 65%, transparent)';

  const hasCategories = categories.length > 0;

  return (
    <section
      className="fp-section pt-12 md:pt-20 pb-12 md:pb-20"
      style={{ '--fp-accent-label': accentLabelColor, '--fp-rule-color': ruleColor } as React.CSSProperties}
    >
      <CtaStyles />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <RichTextContent
          path="floorplanslist.heading"
          as="h2"
          className="fp-heading font-heading text-[clamp(1.75rem,6vw,2.25rem)] sm:text-5xl md:text-4xl lg:text-5xl xl:text-6xl font-medium text-center leading-tight max-w-[17ch] lg:max-w-none lg:whitespace-nowrap mx-auto mb-10 md:mb-16"
        />

        {hasCategories ? (
          <>
            <div ref={gridRef} className="fp-grid flex flex-wrap lg:flex-nowrap justify-center items-start gap-x-4 gap-y-10 sm:gap-8 lg:gap-14">
              {/* Bedroom category circles — each uses a literal groupPath for static linter detection */}
              {categories.length > 0 && (
                <CategoryCard cat={categories[0]}>
                  <EditableSlotGroup groupPath="floorplanslist.card_image_0" definition={catSlotDefs[0]} className="absolute inset-0">
                    <CardImageContent img={catSlotImages[0]} fallbackAlt={`${categories[0].label} floor plans`} />
                  </EditableSlotGroup>
                </CategoryCard>
              )}
              {categories.length > 1 && (
                <CategoryCard cat={categories[1]}>
                  <EditableSlotGroup groupPath="floorplanslist.card_image_1" definition={catSlotDefs[1]} className="absolute inset-0">
                    <CardImageContent img={catSlotImages[1]} fallbackAlt={`${categories[1].label} floor plans`} />
                  </EditableSlotGroup>
                </CategoryCard>
              )}
              {categories.length > 2 && (
                <CategoryCard cat={categories[2]}>
                  <EditableSlotGroup groupPath="floorplanslist.card_image_2" definition={catSlotDefs[2]} className="absolute inset-0">
                    <CardImageContent img={catSlotImages[2]} fallbackAlt={`${categories[2].label} floor plans`} />
                  </EditableSlotGroup>
                </CategoryCard>
              )}
              {categories.length > 3 && (
                <CategoryCard cat={categories[3]}>
                  <EditableSlotGroup groupPath="floorplanslist.card_image_3" definition={catSlotDefs[3]} className="absolute inset-0">
                    <CardImageContent img={catSlotImages[3]} fallbackAlt={`${categories[3].label} floor plans`} />
                  </EditableSlotGroup>
                </CategoryCard>
              )}
              {categories.length > 4 && (
                <CategoryCard cat={categories[4]}>
                  <EditableSlotGroup groupPath="floorplanslist.card_image_4" definition={catSlotDefs[4]} className="absolute inset-0">
                    <CardImageContent img={catSlotImages[4]} fallbackAlt={`${categories[4].label} floor plans`} />
                  </EditableSlotGroup>
                </CategoryCard>
              )}
            </div>

            <div className="mt-12 md:mt-16 flex justify-center">
              <EditableSlotGroup groupPath="floorplanslist.cta_link" definition={ctaLinkDef}>
                <a
                  href={ctaLink.url || '/floor-plans'}
                  target={ctaLink._target || '_self'}
                  className={`fp-cta ${CTA_SOLID}`}
                  style={ctaOverPrimary}
                >
                  {ctaLink.label || 'View Floor Plans'}
                </a>
              </EditableSlotGroup>
            </div>
          </>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-on-primary)]/70">
            <p className="font-body text-lg">Floor plans will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        .fp-section {
          background: var(--vibe-color-primary);
        }
        /* Card sizing. On tablet/desktop the cards share one even row
           (flex-nowrap on the container); below md the row wraps into a single
           column (see mobile override) so each circle gets its own row. */
        .fp-card-item {
          flex: 1 1 0%;
          min-width: 0;
          max-width: 340px;
        }
        /* On mobile, stack the category circles in a single column (one card
           per row) instead of two-up. Cap the width so the circles don't grow
           to full screen width; the container centers them. */
        @media (max-width: 767px) {
          .fp-card-item {
            flex: 1 1 100%;
            max-width: 300px;
          }
        }
        .fp-heading {
          color: var(--vibe-color-text-on-primary);
        }
        .fp-circle {
          border-radius: 9999px;
        }
        /* Subtle darkening over the photo so the white outlined numeral and
           label stay legible across light and dark interior shots. */
        .fp-card-overlay {
          background: rgba(0, 0, 0, 0.18);
          opacity: 1;
        }
        /* Soft drop shadow behind the circle. Hidden at rest, it fades in to a
           steady shadow on hover (no pulsing, no ring). The glow element is
           kept slightly smaller than the circle so its shadow reads as an even
           shadow beneath the card rather than a halo/ring around its edge. */
        .fp-circle-glow {
          position: absolute;
          inset: 6%;
          border-radius: 9999px;
          opacity: 0;
          box-shadow: 10px 14px 26px -5px rgba(0, 0, 0, 0.5);
          transition: opacity 400ms ease, box-shadow 400ms ease;
          pointer-events: none;
        }
        .fp-card:hover .fp-circle-glow {
          opacity: 1;
          box-shadow: 14px 18px 34px -5px rgba(0, 0, 0, 0.62);
        }
        .fp-number {
          font-size: clamp(4rem, 12vw, 12rem);
          font-weight: 500;
          line-height: 1;
          color: transparent;
          -webkit-text-stroke: 2px rgba(255, 255, 255, 0.92);
          text-stroke: 2px rgba(255, 255, 255, 0.92);
          filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.35));
        }
        .fp-label-rule {
          display: block;
          width: 88px;
          height: 1px;
          background: var(--fp-rule-color, color-mix(in srgb, var(--vibe-color-text-on-primary) 65%, transparent));
          transition: opacity 300ms ease;
        }
        /* Hide the top and bottom rule lines when the card is hovered. */
        .fp-card:hover .fp-label-rule {
          opacity: 0;
        }
        .fp-label-text {
          color: var(--vibe-color-text-on-primary);
        }
        /* The Floor Plans CTA size is inherited from the canonical .v-cta (see
           _cta.tsx), shared by every CTA in the theme; color comes from the
           shared CTA_SOLID / ctaOverPrimary styling. */
        /* On hover, fill background + border with the resolved secondary hover
           fill (the --cta-hover-fill variable, published by CtaStyles and
           auto-darkened when a white label wouldn't clear WCAG AA on the raw
           secondary) and keep the label white. Higher specificity than the shared
           .v-cta--solid:hover, and the ::before darkening overlay is suppressed
           so the fill shows. */
        .fp-cta.v-cta--solid:hover {
          color: #ffffff;
          background: var(--cta-hover-fill, var(--vibe-color-secondary));
          border-color: var(--cta-hover-fill, var(--vibe-color-secondary));
        }
        .fp-cta.v-cta--solid:hover::before {
          opacity: 0;
        }
        /* In edit mode, the overlay tint and centered numeral sit on top of the
           image slot group. Let pointer events pass through them so the
           EditableSlotGroup beneath receives hover/click. */
        [data-vibesite-edit-mode="true"] .fp-card-overlay-layer,
        [data-vibesite-edit-mode="true"] .fp-card-number-layer {
          pointer-events: none;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Discover your new home layout.</p>',
    },
  },
  slotGroups: {
    'floorplanslist.cta_link': ctaLinkDef,
    'floorplanslist.card_image_0': cardImage0Def,
    'floorplanslist.card_image_1': cardImage1Def,
    'floorplanslist.card_image_2': cardImage2Def,
    'floorplanslist.card_image_3': cardImage3Def,
    'floorplanslist.card_image_4': cardImage4Def,
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as any).contentSchema = contentSchema;

export default FloorplansList;
